import { useState } from 'react'
import { Row, Col, Card, Input, Select, Button, Badge, Typography } from '../../../ui/ot'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../../../ui/table'
import { Barcode, Hash, TriangleAlert, Trash2, ListOrdered, ArrowRight } from 'lucide-react'
import {
    useCollectionBarcodeCodes, useCollectionBarcodeOverview, barcodeCodeLabel, COLLECTION_BARCODE_CODES, COLLECTION_BARCODE_PREFIX_OPTIONS,
    formatBarcodeCount, validateBarcodeRange, exportBarcodeOverview, printBarcodeReport,
} from '../../controllers/CollectionBarcodeOverviewController'

const emptyInput = prefix => ({ prefix, start_no: '', end_no: '' })
const hintStyle = { color: '#64748b', fontSize: '0.9em', margin: 0 }
const StatBlock = ({ icon, label, value, hint }) => <Card styles={{ padding: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b' }}>{icon}<span>{label}</span></div>
    <div style={{ fontSize: '1.6em', fontWeight: 700, overflowWrap: 'anywhere', margin: '8px 0' }}>{value ?? '-'}</div>
    {hint && <Typography.P style={hintStyle}>{hint}</Typography.P>}
</Card>

const CollectionBarcodeOverview = () => {
    const codes = useCollectionBarcodeCodes()
    const [input, setInput] = useState(() => emptyInput('T'))
    const [query, setQuery] = useState(() => validateBarcodeRange(emptyInput('T')))
    const overview = useCollectionBarcodeOverview(query)
    const [validationError, setValidationError] = useState('')
    const [reportError, setReportError] = useState('')
    const [busy, setBusy] = useState('')
    const codeRows = codes.data?.codes || []
    const result = overview.data
    const summary = result?.summary
    const gaps = result?.gaps?.data || []
    const ready = result?.range?.status === 'ready'
    const total = result?.gaps?.total || '0'
    const page = query?.cur_page || 1
    const size = query?.size || 50
    const pageEnd = String(page * size)
    const lastPage = pageEnd.length > total.length || (pageEnd.length === total.length && pageEnd >= total)

    const search = () => {
        try {
            setQuery(validateBarcodeRange(input))
            setValidationError('')
            setReportError('')
        } catch (error) { setValidationError(error.message) }
    }
    const reset = () => {
        const initial = emptyInput('T')
        setInput(initial)
        setQuery(validateBarcodeRange(initial))
        setValidationError('')
        setReportError('')
    }
    const report = async mode => {
        setBusy(mode)
        setReportError('')
        try {
            if (mode === 'export') await exportBarcodeOverview(query)
            else await printBarcodeReport(mode === 'codes' ? null : query)
        } catch (error) { setReportError(error.message) }
        finally { setBusy('') }
    }
    const appliedCode = COLLECTION_BARCODE_CODES.find(code => code.prefix === query?.prefix)
    const nextHint = result?.next_number?.unavailable_reason === 'number_limit_reached' ? '已達支援的號碼上限'
        : result?.next_number?.unavailable_reason === 'first_number_not_configured' ? '起編號尚未設定'
            : result?.next_number?.unavailable_reason === 'format_not_configured'
                ? `編碼格式尚未設定；建議接續數字 ${result.next_number.candidate_number}` : '依已知紀錄推算，尚未配號'

    return <Row gutter={[8, 12]}>
        <Col span={24}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Barcode className="h-6 w-6" /><Typography.Title level={3} style={{ margin: 0 }}>館藏條碼總覽</Typography.Title>
            </div>
            <Typography.P style={hintStyle}>統計範圍：目前 Koha 來源全館；館藏筆數包含已報廢館藏。</Typography.P>
        </Col>
        <Col span={24}>
            <Card styles={{ padding: 16 }}>
                <Row gutter={[8, 8]} align="middle">
                    <Col><Typography.P style={{ margin: 0 }}>館藏條碼代碼：</Typography.P></Col>
                    <Col style={{ minWidth: 240 }}><Select allowClear={false} placeholder="請選擇代碼" value={input.prefix}
                        onChange={prefix => setInput(previous => ({ ...previous, prefix }))}
                        options={COLLECTION_BARCODE_PREFIX_OPTIONS} /></Col>
                    <Col><Input aria-label="條碼起號" placeholder="起號數字，例如 0001（可不填）" value={input.start_no}
                        onChange={event => setInput(previous => ({ ...previous, start_no: event.target.value }))}
                        onKeyDown={event => { if (event.key === 'Enter') search() }} /></Col>
                    <Col>～</Col>
                    <Col><Input aria-label="條碼迄號" placeholder="迄號數字，例如 1000（可不填）" value={input.end_no}
                        onChange={event => setInput(previous => ({ ...previous, end_no: event.target.value }))}
                        onKeyDown={event => { if (event.key === 'Enter') search() }} /></Col>
                    <Col><Button disabled={!input.prefix} onClick={search}>查詢</Button></Col>
                    <Col><Button type="default" onClick={reset}>清除</Button></Col>
                </Row>
                {validationError && <p role="alert" style={{ color: '#b91c1c' }}>{validationError}</p>}
            </Card>
        </Col>
        {codes.loading && <Col span={24}><p role="status">載入代碼表統計中…</p></Col>}
        {codes.error && <Col span={24}><div role="alert">{codes.error} <Button onClick={codes.reload}>重試代碼表載入</Button></div></Col>}
        {!codes.loading && !codes.error && codeRows.length === 0 && <Col span={24}><p>目前沒有館藏代碼統計資料。</p></Col>}
        {overview.loading && <Col span={24}><p role="status">查詢館藏條碼中…</p></Col>}
        {overview.error && <Col span={24}><div role="alert">{overview.error} <Button onClick={() => setQuery(previous => ({ ...previous }))}>重試查詢</Button></div></Col>}
        {reportError && <Col span={24}><p role="alert" style={{ color: '#b91c1c' }}>{reportError}</p></Col>}
        {result && <>
            <Col span={24}>
                <Typography.P style={hintStyle}>已套用：{appliedCode ? barcodeCodeLabel(appliedCode) : query.prefix}；數字範圍 {result.range.start_number ?? '未定'} ～ {result.range.end_number ?? '未定'}；資料時間 {result.generated_at}</Typography.P>
            </Col>
            <Col span={24}>
                <Row gutter={[8, 8]}>
                    {[
                        { label: '範圍內最小號', value: summary.min_no, icon: <Hash size={16} /> },
                        { label: '範圍內最大號', value: summary.max_no, icon: <Hash size={16} /> },
                        { label: '現有館藏筆數', value: formatBarcodeCount(summary.used), hint: `含已報廢 ${formatBarcodeCount(summary.withdrawn)} 筆`, icon: <ListOrdered size={16} /> },
                        { label: '空號數量（目前無館藏）', value: formatBarcodeCount(summary.blank), hint: `共 ${formatBarcodeCount(summary.gap_ranges)} 個區段`, icon: <TriangleAlert size={16} /> },
                        { label: '已報廢筆數', value: formatBarcodeCount(summary.withdrawn), hint: '仍占用原號碼，不列入空號', icon: <Trash2 size={16} /> },
                        { label: '建議接續號碼', value: result.next_number.candidate, hint: nextHint, icon: <ArrowRight size={16} /> },
                    ].map(stat => <Col key={stat.label} xxl={4} xl={8} lg={8} md={12} sm={12} xs={24}><StatBlock {...stat} /></Col>)}
                </Row>
            </Col>
            <Col span={24}>
                <p role="note" style={{ color: '#92400e', margin: '4px 0' }}>{result.history.warning}</p>
                <Typography.P style={hintStyle}>現有條碼保留原格式；空號區段依代碼設定補零，尚未設定時顯示未補零序號。</Typography.P>
                {(result.quality.invalid_barcode_rows_source_total !== '0' || result.quality.selected_prefix_collision_excess_rows !== '0' || result.quality.reappeared_historical_numbers !== '0') &&
                    <p role="note">資料品質：全館無法解析條碼 {formatBarcodeCount(result.quality.invalid_barcode_rows_source_total)} 列；此代碼全範圍序號碰撞額外 {formatBarcodeCount(result.quality.selected_prefix_collision_excess_rows)} 列；曾刪除號碼再次出現 {formatBarcodeCount(result.quality.reappeared_historical_numbers)} 個。</p>}
            </Col>
            <Col span={24}><Card styles={{ padding: 16 }}>
                <Row justify="space-between" align="middle" gutter={[8, 8]}>
                    <Col><Typography.Title level={4}>空號清單</Typography.Title></Col>
                    <Col><div style={{ display: 'flex', gap: 8 }}>
                        <Button disabled={!ready || !!busy} onClick={() => report('export')}>{busy === 'export' ? '匯出中…' : '匯出 Excel'}</Button>
                        <Button type="default" disabled={!ready || !!busy} onClick={() => report('gaps')}>{busy === 'gaps' ? '準備列印…' : '列印空號報表'}</Button>
                    </div></Col>
                </Row>
                {result.range.status === 'undetermined' ? <p>無法推得完整範圍，請輸入完整起迄號。</p>
                    : result.range.status === 'empty' ? <p>條件內無可計算區間，請調整起迄號。</p>
                        : <>
                            <Table><TableHeader><TableRow>
                                {['起號', '迄號', '空號數量', '原因', '保留狀態'].map(label => <TableHead key={label}>{label}</TableHead>)}
                            </TableRow></TableHeader><TableBody>
                                {gaps.map(gap => <TableRow key={gap.start_number}>
                                    <TableCell style={{ overflowWrap: 'anywhere' }}>{gap.start}</TableCell>
                                    <TableCell style={{ overflowWrap: 'anywhere' }}>{gap.end}</TableCell>
                                    <TableCell>{formatBarcodeCount(gap.count)}</TableCell>
                                    <TableCell><Badge>{gap.reserved ? '有刪除紀錄，曾使用' : '目前無館藏，原因未確認'}</Badge></TableCell>
                                    <TableCell>{gap.reserved ? '保留，不重配' : '未確認；不代表可重用'}</TableCell>
                                </TableRow>)}
                                {gaps.length === 0 && <TableRow><TableCell colSpan={5}>{total === '0' ? '此範圍目前沒有空號' : '此頁沒有資料，請回上一頁或重新查詢'}</TableCell></TableRow>}
                            </TableBody></Table>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
                                <Button type="default" disabled={page <= 1} onClick={() => setQuery(previous => ({ ...previous, cur_page: previous.cur_page - 1 }))}>上一頁</Button>
                                <span>第 {page} 頁，共 {formatBarcodeCount(result.gaps.total)} 個區段</span>
                                <Button type="default" disabled={lastPage} onClick={() => setQuery(previous => ({ ...previous, cur_page: previous.cur_page + 1 }))}>下一頁</Button>
                            </div>
                        </>}
                <Typography.P style={hintStyle}>已報廢館藏仍占用原號碼；有刪除證據的號碼保留不重配。原因未知的空號也不代表可以重用。匯出及列印會依已套用條件重新查詢全部區段，資料時間可能與本畫面不同。</Typography.P>
            </Card></Col>
        </>}
        {codes.data && <Col span={24}><Card styles={{ padding: 16 }}>
            <Row justify="space-between" align="middle" gutter={[8, 8]}>
                <Col><Typography.Title level={4}>館藏代碼表</Typography.Title></Col>
                <Col><Button type="default" disabled={!!busy || codeRows.length === 0} onClick={() => report('codes')}>{busy === 'codes' ? '準備列印…' : '列印代碼表'}</Button></Col>
            </Row>
            <Typography.P style={hintStyle}>全代碼統計，不受上方起迄條件影響；資料時間 {codes.data.generated_at}。未設定名稱的代碼仍可查詢。</Typography.P>
            <Table><TableHeader><TableRow>{['代碼', '館藏類型', '說明', '目前最大號', '現有筆數（含報廢）'].map(label => <TableHead key={label}>{label}</TableHead>)}</TableRow></TableHeader>
                <TableBody>{codeRows.map(code => <TableRow key={code.prefix}>
                    <TableCell><Badge>{code.prefix === '__NONE__' ? '（無代碼）' : code.prefix}</Badge>{!code.enabled && '（設定停用）'}</TableCell>
                    <TableCell>{code.display_name}</TableCell><TableCell>{code.description || '-'}</TableCell>
                    <TableCell style={{ overflowWrap: 'anywhere' }}>{code.max_no ?? '-'}</TableCell><TableCell>{formatBarcodeCount(code.used)}</TableCell>
                </TableRow>)}</TableBody>
            </Table>
        </Card></Col>}
    </Row>
}

export default CollectionBarcodeOverview
